import {
  hasCrossMarginRisk,
  hasSubAccounts,
  isMultiMarket,
} from '@1delta/lender-registry'
import { deriveBorrowTags, deriveSupplyTags } from './tags'
import {
  borrowDescription,
  borrowHeadline,
  supplyDescription,
  supplyHeadline,
} from './format'
import { DEFAULT_PROFILE_ID } from './profiles'
import type { TermConfigEntry, TermSheetInput } from './input'
import type {
  AvailabilityTerms,
  BorrowTermSheet,
  CoverageInfo,
  DeepPartial,
  ExposureTerms,
  FeeTerm,
  LiquidationTerms,
  MaturityTerms,
  ModeVariant,
  PositionConstraints,
  RateKind,
  RateTerms,
  RewardTerm,
  SupplyTermSheet,
  TermAssetRef,
  TermSheet,
  UtilizationTerms,
} from './types'
import { TERM_SHEET_SCHEMA_VERSION } from './types'

/**
 * The generic builder.
 *
 * Runs over EVERY market and produces a complete, correct term sheet from the
 * fields the converters already emit. Per-lender adapters (see `adapters/`)
 * then merge a `DeepPartial<TermSheet>` on top for the facts that cannot be
 * derived. That split is what makes ~200 Aave/Compound forks free: they never
 * need an adapter at all.
 */

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

const isNum = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v)

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

function assetRef(input: TermSheetInput): TermAssetRef {
  const a = input.asset ?? {}
  return {
    chainId: String(a.chainId ?? input.chainId),
    address: String(a.address ?? input.underlying ?? '').toLowerCase(),
    symbol: a.symbol,
    name: a.name,
    decimals: a.decimals ?? input.decimals,
    assetGroup: a.assetGroup,
    logoURI: a.logoURI,
  }
}

/** `config['0']` is the default category on every lender we support. */
function defaultConfig(input: TermSheetInput): TermConfigEntry | undefined {
  const cfg = input.config
  if (!cfg) return undefined
  return cfg['0'] ?? Object.values(cfg)[0]
}

// ---------------------------------------------------------------------------
// rate
// ---------------------------------------------------------------------------

const RATE_MODEL_TO_KIND: Record<string, RateKind> = {
  variable: 'variable-curve',
  fixedTerm: 'fixed-term',
  userSet: 'user-set',
  zeroInterest: 'zero-interest',
  dbr: 'prepaid',
  protocolSet: 'variable-managed',
}

function rateKindOf(input: TermSheetInput): RateKind {
  const mapped = input.rateModel
    ? RATE_MODEL_TO_KIND[input.rateModel]
    : undefined
  return mapped ?? 'variable-curve'
}

function rewardTerms(
  input: TermSheetInput,
  side: 'supply' | 'borrow',
): RewardTerm[] | undefined {
  if (!input.rewards?.length) return undefined
  const out: RewardTerm[] = []
  for (const r of input.rewards) {
    const apr =
      side === 'supply'
        ? (r.depositRate ?? 0)
        : (r.variableBorrowRate ?? r.stableBorrowRate ?? 0)
    if (!apr) continue
    const isPoints = r.kind === 'points' || !r.asset
    out.push({
      asset: r.asset
        ? { chainId: input.chainId, address: String(r.asset).toLowerCase() }
        : undefined,
      kind: isPoints ? 'points' : 'token',
      apr,
      side,
      claim:
        (r.claim as RewardTerm['claim']) ?? (isPoints ? 'none' : 'accrual'),
      endsAt: r.endsAt,
      // Points have no priceable value, so they must not inflate `aprTotal`.
      indicative: isPoints || undefined,
    })
  }
  return out.length ? out : undefined
}

function buildRate(
  input: TermSheetInput,
  side: 'supply' | 'borrow',
): RateTerms {
  const kind = rateKindOf(input)
  const rewards = rewardTerms(input, side)
  const priceableRewards = (rewards ?? [])
    .filter((r) => !r.indicative)
    .reduce((s, r) => s + r.apr, 0)

  // Fixed-rate lenders park the best fixed borrow APR in the `stableBorrowRate`
  // slot (a convention the converters share). Prefer the rate menu when it
  // exists so the headline matches the card.
  const menuBest = input.terms?.length
    ? Math.min(...input.terms.map((t) => t.apr))
    : undefined

  let base: number
  if (side === 'supply') {
    base = input.depositRate ?? 0
  } else if (kind === 'fixed-term') {
    base = menuBest ?? input.stableBorrowRate ?? input.variableBorrowRate ?? 0
  } else {
    base = input.variableBorrowRate ?? 0
  }

  const intrinsic = side === 'supply' ? (input.intrinsicYield ?? 0) : 0

  return {
    kind,
    apr: base,
    components: {
      base,
      rewards: priceableRewards || undefined,
      intrinsic: intrinsic || undefined,
    },
    aprTotal: base + priceableRewards + intrinsic,
    basis: 'apr-nominal',
    compounding:
      kind === 'zero-interest' || kind === 'none' ? 'none' : 'per-second',
    source:
      kind === 'fixed-term'
        ? input.fixedTerm?.provider?.kind === 'orderbook'
          ? 'orderbook'
          : input.fixedTerm?.provider?.kind === 'auction'
            ? 'auction'
            : 'derived'
        : kind === 'variable-managed'
          ? 'governance'
          : kind === 'user-set'
            ? 'borrower'
            : kind === 'nav-accrual'
              ? 'oracle'
              : 'utilization-curve',
    isLocked: kind === 'fixed-term' || kind === 'fixed-open',
    rewards,
    menu: input.terms?.length ? input.terms.map((t) => ({ ...t })) : undefined,
  }
}

// ---------------------------------------------------------------------------
// maturity
// ---------------------------------------------------------------------------

function buildMaturity(input: TermSheetInput, now: number): MaturityTerms {
  const ft = input.fixedTerm
  const maturity = ft?.maturity

  if (isNum(maturity) && maturity > 0) {
    return {
      kind: 'fixed-date',
      maturity,
      maturityIso: new Date(maturity * 1000).toISOString(),
      secondsToMaturity: Math.max(0, maturity - now),
    }
  }

  // A rate MENU alone is enough to know the term is rolling — the fixed-term
  // descriptor is not always attached to the row (see originShape.test.ts).
  if (input.terms?.length) {
    const durations = input.terms.map((t) => t.durationSecs).filter(isNum)
    return {
      kind: 'rolling-duration',
      minDurationSecs: durations.length ? Math.min(...durations) : undefined,
      maxDurationSecs: durations.length ? Math.max(...durations) : undefined,
    }
  }

  return { kind: 'perpetual' }
}

// ---------------------------------------------------------------------------
// fees
// ---------------------------------------------------------------------------

function buildFees(
  input: TermSheetInput,
  side: 'supply' | 'borrow',
): FeeTerm[] {
  const out: FeeTerm[] = []
  const ft = input.fixedTerm

  if (side === 'borrow') {
    const origination =
      input.originationFee ?? ft?.fees?.originationFeePercent ?? undefined
    if (isNum(origination) && origination !== 0) {
      out.push({
        id: 'origination',
        label: 'Origination fee',
        when: 'entry',
        unit: 'percent',
        basis: 'principal',
        value: origination,
        payee: 'protocol',
        description:
          'Charged once at borrow time as a percent of the amount borrowed. Not an APR — never add it to the rate.',
      })
    }
    if (isNum(ft?.fees?.latePenaltyApr) && ft.fees.latePenaltyApr !== 0) {
      out.push({
        id: 'late-penalty',
        label: 'Late-repayment penalty',
        when: 'late',
        unit: 'apr-percent',
        basis: 'face-value',
        value: ft.fees.latePenaltyApr,
        payee: 'lenders',
        mutable: true,
        description:
          'Accrues on overdue debt after maturity until repaid. Simple interest on the face value, not compounding.',
      })
    }
    if (ft?.earlyRepay?.kind === 'discount') {
      out.push({
        id: 'early-repay-discount',
        label: 'Early-repayment rebate',
        when: 'exit',
        unit: 'percent',
        basis: 'face-value',
        // Negative = rebate. Sign is load-bearing.
        value: -0,
        indicative: true,
        payee: 'protocol',
        description:
          'Repaying before maturity costs LESS than face value. The exact rebate is resolved at repay time and can be zero.',
      })
    }
  }

  if (side === 'supply') {
    if (isNum(ft?.fees?.continuousFeeApr) && ft.fees.continuousFeeApr !== 0) {
      out.push({
        id: 'continuous',
        label: 'Continuous lender fee',
        when: 'ongoing',
        unit: 'apr-percent',
        basis: 'principal',
        value: ft.fees.continuousFeeApr,
        payee: 'protocol',
        description: 'An ongoing haircut taken from the lender side.',
      })
    }
    if (isNum(ft?.fees?.settlementFee) && ft.fees.settlementFee !== 0) {
      out.push({
        id: 'settlement',
        label: 'Settlement fee',
        when: 'exit',
        unit: 'percent',
        basis: 'principal',
        value: ft.fees.settlementFee * 100,
        payee: 'protocol',
      })
    }
  }

  return out
}

// ---------------------------------------------------------------------------
// availability / utilization / constraints
// ---------------------------------------------------------------------------

/**
 * Minimum borrow / minimum debt, in RAW base units.
 *
 * Every lender that has one calls it something different and hides it
 * somewhere different, and it is a HARD gate — a borrow below it reverts. It
 * is also two questions at once, which is why the result is written to both
 * `availability.minSize` (you cannot OPEN below this) and `exit.minDebt` (you
 * cannot LEAVE a position below this, so a partial repay is bounded).
 */
function resolveMinDebt(input: TermSheetInput): string | undefined {
  const m = input.market ?? {}
  const candidates: unknown[] = [
    // Compound V3 `baseBorrowMin`, read straight off the Comet.
    input.minBorrow,
    // Morpho Blue / Lista brokered markets.
    (m as Record<string, any>).minLoan,
    // Liquity family.
    (m as Record<string, any>).liquity?.minDebt,
    // River (Satoshi).
    (m as Record<string, any>).river?.minNetDebt,
    // Inverse FiRM.
    (m as Record<string, any>).inverse?.minDebt,
    // Cooler (Olympus V2) — `minDebtRequired`, 1,000 USDS. Gates borrows AND
    // partial repays: any action leaving 0 < debt < minDebt reverts.
    (m as Record<string, any>).cooler?.minDebt,
    // dss (Sky / the USDD fork): `dust` IS the per-vault debt floor.
    (m as Record<string, any>).dss?.dust ??
      (m as Record<string, any>).usdd?.dust,
    // Gearbox credit facade.
    (m as Record<string, any>).gearbox?.minDebt,
    // Resupply — a hard per-position floor that also bounds partial repayment.
    (m as Record<string, any>).resupply?.minimumBorrowAmount,
  ]
  for (const c of candidates) {
    if (c == null) continue
    const str = String(c)
    // `0` means "no floor", not "a floor of zero" — do not report it.
    if (str === '0' || str === '') continue
    if (!/^\d+$/.test(str)) continue
    return str
  }
  return undefined
}

/**
 * Minimum COLLATERAL to open, raw base units of the collateral asset.
 *
 * Distinct from {@link resolveMinDebt}: some lenders gate on the collateral
 * leg instead of the debt leg, and Frankencoin does BOTH nothing on debt and
 * a hard floor on collateral — falling below it closes the position for good.
 */
function resolveMinCollateral(input: TermSheetInput): string | undefined {
  const m = (input.market ?? {}) as Record<string, any>
  const raw = m.frankencoin?.minimumCollateral
  if (raw == null) return undefined
  const str = String(raw)
  if (str === '0' || str === '' || !/^\d+$/.test(str)) return undefined
  return str
}

function capUtil(total: number | undefined, cap: number | undefined) {
  if (!isNum(cap) || cap <= 0 || !isNum(total)) return undefined
  return clamp01(total / cap)
}

function buildAvailability(
  input: TermSheetInput,
  side: 'supply' | 'borrow',
): AvailabilityTerms {
  const frozen = input.isFrozen === true
  const inactive = input.isActive === false
  const enabled =
    side === 'supply'
      ? input.depositsEnabled !== false
      : input.borrowingEnabled !== false

  const cap = side === 'supply' ? input.supplyCap : input.borrowCap
  const total = side === 'supply' ? input.totalDeposits : input.totalDebt
  const capUtilization = capUtil(total, cap)
  const capFull = capUtilization != null && capUtilization >= 1

  const noLiquidity =
    side === 'borrow' &&
    isNum(input.borrowLiquidity) &&
    input.borrowLiquidity <= 0

  const canOpen = enabled && !frozen && !inactive && !capFull && !noLiquidity

  let blockedBy: AvailabilityTerms['blockedBy']
  if (!canOpen) {
    if (inactive) blockedBy = 'shutdown'
    else if (frozen) blockedBy = 'frozen'
    else if (!enabled) blockedBy = 'disabled'
    else if (capFull) blockedBy = 'cap-full'
    else if (noLiquidity) blockedBy = 'no-liquidity'
  }

  return {
    canOpen,
    // Closing a position stays possible in every wind-down we have seen —
    // freezes stop entry, not exit. Adapters override where that is false.
    canClose: true,
    blockedBy,
    gating: 'permissionless',
    // A borrow below the protocol's floor REVERTS, so gate the CTA on it
    // rather than letting the transaction fail.
    minSize:
      side === 'borrow' ? resolveMinDebt(input) : resolveMinCollateral(input),
    cap: isNum(cap) && cap > 0 ? String(cap) : undefined,
    capUtilization,
    requires: side === 'supply' ? ['token-approval'] : undefined,
  }
}

function buildUtilization(input: TermSheetInput): UtilizationTerms | undefined {
  if (!isNum(input.utilization)) return undefined
  const shared = isNum(input.irmTotalDeposits) && isNum(input.irmTotalDebt)
  return {
    utilization: clamp01(input.utilization),
    basis: shared ? 'pool' : 'market',
    irmTotalDeposits: input.irmTotalDeposits,
    irmTotalDebt: input.irmTotalDebt,
    supplyCapUtilization: capUtil(input.totalDeposits, input.supplyCap),
    borrowCapUtilization: capUtil(input.totalDebt, input.borrowCap),
    lockupRatio: input.lockupRatio,
  }
}

/**
 * Position constraints — DERIVED from lender state, never hardcoded.
 *
 * The first cut of this returned `crossMargin: true` and
 * `positionModel: 'account'` for everything and relied on adapters to correct
 * it. That is backwards: only ~13 lenders have an adapter, so every lender
 * without one silently reported the wrong answer — Morpho Blue and Silo as
 * cross-margin when they are isolated pairs, Dolomite / Init / Euler as
 * single-position when they are sub-account lenders.
 *
 * The registry already answers both questions for the whole lender space, and
 * documents them as deliberately ORTHOGONAL (`hasCrossMarginRisk` /
 * `hasSubAccounts` / `isMultiMarket` — all four combinations exist). So the
 * family truth comes from there, and the OBSERVED counts from this fetch are
 * carried alongside as evidence rather than as a competing source.
 */
function buildConstraints(
  input: TermSheetInput,
  siblings: TermSheetInput[],
  acceptedCollateral: ExposureTerms | undefined,
): PositionConstraints {
  const ceiling = input.debtCeiling
  const ceilingNum = isNum(Number(ceiling)) ? Number(ceiling) : 0
  const lender = input.lender ?? ''

  // Real counts from THIS fetch, not an assumption.
  const collateralAssetCount = acceptedCollateral?.count ?? 0
  const marketCount = siblings.filter((s) => s.marketUid).length || 1

  return {
    isolation:
      ceilingNum > 0
        ? { enabled: true, debtCeiling: String(ceiling) }
        : undefined,
    crossMargin: hasCrossMarginRisk(lender),
    // A sub-account lender gets the honest generic answer; its adapter refines
    // it to the concrete shape (`nft` / `cdp-id` / `loan-id` / `escrow`).
    positionModel: hasSubAccounts(lender) ? 'sub-account' : 'account',
    observed: {
      collateralAssetCount,
      marketCount,
      multiMarketKey: isMultiMarket(lender),
    },
  }
}

// ---------------------------------------------------------------------------
// liquidation + modes
// ---------------------------------------------------------------------------

/**
 * Market-level LLTV (WAD) — the ISOLATED-market signal.
 *
 * Isolated two-asset lenders (Morpho, Midnight, Silo, LlamaLend) publish the
 * loan-to-value on `params.market.lltv`. Cross-margin pool lenders do not
 * (Exactly emits `'0'`), which makes a non-zero value here a reliable marker
 * that this market has exactly one collateral and therefore ONE well-defined
 * LTV.
 */
function marketLltv(input: TermSheetInput): number | undefined {
  const raw = (input.market as Record<string, any> | undefined)?.lltv
  if (raw == null) return undefined
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return n / 1e18
}

function liquidationFrom(
  cfg: TermConfigEntry | undefined,
  input: TermSheetInput,
  acceptedCollateral?: ExposureTerms,
): LiquidationTerms {
  // On an ISOLATED market the risk parameters live on the COLLATERAL leg; the
  // loan leg's own config is hardcoded to zero because the borrowed asset is
  // not collateral in that market. Reading the row's own config there reports
  // "Max LTV 0% · Liquidation at 0% · Liquidator takes 0%" for a market that
  // actually runs at 86% — the numbers were on the sibling all along.
  //
  // Scoped to isolated markets deliberately: on a cross-margin pool the
  // borrow-side LTV is not a property of the borrowed asset at all (it depends
  // on the collateral you post), so borrowing a sibling's factor there would
  // trade one wrong number for another.
  const lltv = marketLltv(input)
  const soleCollateral =
    acceptedCollateral?.count === 1 ? acceptedCollateral.items[0] : undefined
  // `count === 1` alone is NOT the isolated signal: the accepted set excludes
  // the row itself, so a cross-margin pool with two listed reserves would also
  // report one. The registry's cross-margin flag is the authority; a
  // market-level LLTV is the other, independent one.
  const isolated =
    lltv != null ||
    (!hasCrossMarginRisk(input.lender) && soleCollateral != null)

  const ownLtv = cfg?.borrowCollateralFactor
  const ownLiqLtv = cfg?.collateralFactor
  const ownPenalty = cfg?.liquidationPenalty

  return {
    // The ordinary model: a liquidator repays part of the debt and takes
    // collateral plus a bonus. Adapters override for the four lenders where
    // that description is simply not what happens.
    model: 'repay-seize',
    absorber: 'liquidator',
    trigger: 'price',
    ltv:
      ownLtv ||
      (isolated ? (soleCollateral?.ltv ?? lltv) : undefined) ||
      ownLtv,
    liquidationLtv:
      ownLiqLtv ||
      (isolated ? (soleCollateral?.liquidationLtv ?? lltv) : undefined) ||
      ownLiqLtv,
    // No `?? 0` — see `LiquidationTerms.penalty`. An unknown penalty is
    // undefined so the row is omitted rather than rendered as "+0%".
    penalty:
      ownPenalty ||
      (isolated ? soleCollateral?.liquidationPenalty : undefined) ||
      ownPenalty,
    closeFactor: cfg?.closeFactor ?? input.closeFactor ?? 1,
    targetHealthFactor: cfg?.targetHealthFactor ?? input.targetHealthFactor,
    seizure: 'proportional',
    // A shortfall has to land somewhere; `socializedLoss` on the counterparty
    // is the only signal the generic path has, and adapters refine it.
    badDebt: 'unknown',
  }
}

/**
 * Non-default categories as DELTAS. `config` is a MAP (Aave e-modes, Dolomite
 * categories, Euler configs, Silo) — a single flat LTV silently reports the
 * default and hides the rest, which on an Aave ETH-correlated e-mode is the
 * difference between 80 % and 93 %.
 */
function buildModes(input: TermSheetInput): ModeVariant[] | undefined {
  const cfg = input.config
  if (!cfg) return undefined
  const keys = Object.keys(cfg)
  if (keys.length <= 1) return undefined

  const out: ModeVariant[] = []
  for (const key of keys) {
    const c = cfg[key]
    if (!c) continue
    const isDefault = key === '0'
    out.push({
      modeId: key,
      label: c.label,
      isDefault,
      entry: isDefault ? undefined : 'user-selected',
      liquidation: {
        ltv: c.borrowCollateralFactor,
        liquidationLtv: c.collateralFactor,
        // Same rule as the top-level penalty: unknown stays unknown.
        penalty: c.liquidationPenalty,
        closeFactor: c.closeFactor ?? input.closeFactor ?? 1,
        targetHealthFactor: c.targetHealthFactor ?? input.targetHealthFactor,
      },
      availability: c.debtDisabled ? { canOpen: false } : undefined,
    })
  }
  return out.length > 1 ? out : undefined
}

// ---------------------------------------------------------------------------
// exposures
// ---------------------------------------------------------------------------

/**
 * Accepted-collateral / backing set from the SIBLING rows of the same lender.
 *
 * Pooled lenders (Aave, Compound) do NOT record on-chain which collateral
 * backs which borrow, so the result is the ACCEPTED SET with
 * `weightBasis: 'unweighted'` and NO `weightPct` on any item. Inventing a
 * TVL-proxy weight would look authoritative and be false — a large idle
 * collateral market is not a large exposure.
 */
export function buildExposures(
  input: TermSheetInput,
  siblings: TermSheetInput[],
  direction: 'backing' | 'accepted',
): ExposureTerms | undefined {
  const cfg = input.config
  if (!cfg || siblings.length === 0) return undefined

  const categories = Object.keys(cfg)
  const items: ExposureTerms['items'] = []

  for (const sib of siblings) {
    if (sib.marketUid === input.marketUid) continue
    const sibCfg = sib.config
    if (!sibCfg) continue

    // A sibling is a COLLATERAL candidate for this row when it is usable as
    // collateral in a category this row can carry debt in.
    let best: TermConfigEntry | undefined
    for (const cat of categories) {
      const here = cfg[cat]
      const there = sibCfg[cat]
      if (!here || !there) continue
      if (direction === 'accepted' || direction === 'backing') {
        // this row is the DEBT leg, the sibling is the COLLATERAL leg
        if (here.debtDisabled === true) continue
        if (there.collateralDisabled === true) continue
      }
      if (!best || (there.collateralFactor ?? 0) > (best.collateralFactor ?? 0))
        best = there
    }
    if (!best) continue

    items.push({
      asset: assetRef(sib),
      marketUid: sib.marketUid,
      via: 'collateral',
      ltv: best.borrowCollateralFactor,
      liquidationLtv: best.collateralFactor,
      liquidationPenalty: best.liquidationPenalty,
    })
  }

  if (items.length === 0) return undefined

  return {
    count: items.length,
    weightBasis: 'unweighted',
    items,
  }
}

// ---------------------------------------------------------------------------
// sides
// ---------------------------------------------------------------------------

function buildSupply(
  input: TermSheetInput,
  now: number,
  siblings: TermSheetInput[],
): SupplyTermSheet | undefined {
  const canBeCollateral = input.collateralActive !== false
  const earns = (input.depositRate ?? 0) > 0 || (input.intrinsicYield ?? 0) > 0
  // A row with neither a deposit surface nor a collateral surface has no
  // supply side at all.
  if (input.depositsEnabled === false && !canBeCollateral && !earns)
    return undefined

  const rate = buildRate(input, 'supply')
  const maturity = buildMaturity(input, now)
  const fees = buildFees(input, 'supply')
  const availability = buildAvailability(input, 'supply')

  const liquidity = isNum(input.totalLiquidity)
    ? {
        assets: input.totalLiquidity,
        assetsUsd: input.totalLiquidityUsd,
        ratio:
          isNum(input.totalDeposits) && input.totalDeposits > 0
            ? clamp01(input.totalLiquidity / input.totalDeposits)
            : undefined,
      }
    : undefined

  const supply: SupplyTermSheet = {
    role: canBeCollateral ? (earns ? 'both' : 'collateral') : 'yield',
    rate,
    maturity,
    exit: {
      mode: 'instant',
      settlement: 'sync',
      liquidity,
      partialAllowed: true,
      priceRisk: 'none',
      fees: fees.filter((f) => f.when === 'exit' || f.when === 'performance'),
    },
    fees,
    backedBy: buildExposures(input, siblings, 'backing'),
    modes: buildModes(input),
    counterparty: {
      kind: 'pool',
      solvency: 'overcollateralized',
    },
    availability,
    principal: { protected: true, risks: [] },
    info: { headline: '', description: '', tags: [] },
  }
  return supply
}

function buildBorrow(
  input: TermSheetInput,
  now: number,
  siblings: TermSheetInput[],
): BorrowTermSheet | undefined {
  const cfg = defaultConfig(input)
  const debtDisabled = cfg?.debtDisabled === true
  // No borrow side when the protocol says so, or when this row is
  // collateral-only (Compound V3 collateral rows, CDP collateral legs).
  if (input.borrowingEnabled === false && debtDisabled) return undefined
  if (
    debtDisabled &&
    (input.totalDebt ?? 0) === 0 &&
    input.borrowingEnabled !== true
  )
    return undefined

  const rate = buildRate(input, 'borrow')
  const maturity = buildMaturity(input, now)
  const fees = buildFees(input, 'borrow')
  // Derived first: on an isolated market it carries the risk parameters the
  // loan row itself does not have.
  const acceptedCollateral = buildExposures(input, siblings, 'accepted')

  const borrow: BorrowTermSheet = {
    rate,
    maturity,
    debtShape:
      rate.kind === 'prepaid'
        ? 'prepaid'
        : rate.kind === 'fixed-term'
          ? 'static-face'
          : 'accruing',
    exit: {
      earlyRepay: 'free',
      atMaturityCost: rate.kind === 'fixed-term' ? 'face' : 'accrued',
      lateBehaviour: 'none',
      partialAllowed: true,
      // The same floor, read as a DUST limit: a partial repay may not leave
      // the position below it.
      minDebt: resolveMinDebt(input),
      fees: fees.filter((f) => f.when === 'exit' || f.when === 'late'),
    },
    liquidation: liquidationFrom(cfg, input, acceptedCollateral),
    acceptedCollateral,
    modes: buildModes(input),
    fees,
    counterparty: { kind: 'pool', solvency: 'overcollateralized' },
    availability: buildAvailability(input, 'borrow'),
    info: { headline: '', description: '', tags: [] },
  }
  return borrow
}

// ---------------------------------------------------------------------------
// merge + finalize
// ---------------------------------------------------------------------------

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Deep merge an adapter's partial over the generic result. Arrays REPLACE. */
export function mergeDeep<T>(base: T, patch: DeepPartial<T> | undefined): T {
  if (!patch) return base
  if (!isPlainObject(base) || !isPlainObject(patch)) return (patch as T) ?? base
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) }
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
    if (v === undefined) continue
    const cur = out[k]
    out[k] =
      isPlainObject(v) && isPlainObject(cur) ? mergeDeep(cur, v as never) : v
  }
  return out as T
}

/**
 * Fill `info` (headline / description / tags) LAST, after adapters have run,
 * so the prose always describes the final values rather than the generic
 * guess. This is the mechanism that stops copy drifting from numbers.
 */
export function finalizeInfo(sheet: TermSheet): TermSheet {
  const market = { governance: sheet.governance, oracle: sheet.oracle }
  // `info` is guaranteed by the builder, but a hand-assembled sheet (tests,
  // future producers) may omit it — degrade rather than throw.
  if (sheet.supply && !sheet.supply.info)
    sheet.supply.info = { headline: '', description: '', tags: [] }
  if (sheet.borrow && !sheet.borrow.info)
    sheet.borrow.info = { headline: '', description: '', tags: [] }
  if (sheet.supply) {
    sheet.supply.info = {
      headline:
        sheet.supply.info.headline || supplyHeadline(sheet.supply, sheet),
      description:
        sheet.supply.info.description || supplyDescription(sheet.supply, sheet),
      implications: sheet.supply.info.implications,
      tags: deriveSupplyTags(sheet.supply, market),
    }
  }
  if (sheet.borrow) {
    sheet.borrow.info = {
      headline: sheet.borrow.info.headline || borrowHeadline(sheet.borrow),
      description:
        sheet.borrow.info.description || borrowDescription(sheet.borrow),
      implications: sheet.borrow.info.implications,
      tags: deriveBorrowTags(sheet.borrow, market),
    }
  }
  return sheet
}

function buildCoverage(sheet: TermSheet): CoverageInfo {
  const present: string[] = []
  const notApplicable: Record<string, string> = {}
  const pending: Record<string, string> = {}

  if (sheet.supply) present.push('supply')
  else notApplicable.supply = 'no-supply-side'
  if (sheet.borrow) present.push('borrow')
  else notApplicable.borrow = 'no-borrow-side'
  if (sheet.utilization) present.push('utilization')
  if (sheet.constraints) present.push('constraints')

  // Governance and oracle are join-time enrichment at the serving layer. A
  // missing block must never read as "this market has no oracle".
  if (sheet.oracle) present.push('oracle')
  else if (sheet.borrow?.liquidation.trigger === 'time')
    notApplicable.oracle = 'time-based-liquidation'
  else pending.oracle = 'not-classified-for-this-lender'

  if (sheet.governance) present.push('governance')
  else pending.governance = 'screener-has-no-rows-for-this-market'

  return {
    present,
    notApplicable: Object.keys(notApplicable).length
      ? notApplicable
      : undefined,
    pending: Object.keys(pending).length ? pending : undefined,
  }
}

export interface BuildTermSheetOptions {
  /** Unix seconds; injected so tests are deterministic. */
  now?: number
  /** Other rows of the SAME lender+chain — used to derive the exposure set. */
  siblings?: TermSheetInput[]
  /** Adapter output, merged over the generic result. */
  patch?: DeepPartial<TermSheet>
  profileId?: string
}

/** Build one complete term sheet for one market row. */
export function buildTermSheet(
  input: TermSheetInput,
  opts: BuildTermSheetOptions = {},
): TermSheet {
  const now = opts.now ?? Math.floor(Date.now() / 1000)
  const siblings = opts.siblings ?? []

  const base: TermSheet = {
    schemaVersion: TERM_SHEET_SCHEMA_VERSION,
    asOf: now,
    profileId: opts.profileId ?? DEFAULT_PROFILE_ID,
    marketUid: input.marketUid,
    lender: input.lender,
    chainId: input.chainId,
    asset: assetRef(input),
    supply: buildSupply(input, now, siblings),
    borrow: buildBorrow(input, now, siblings),
    utilization: buildUtilization(input),
    constraints: undefined,
  }
  // Needs the derived exposure set, so it runs after the sides.
  base.constraints = buildConstraints(
    input,
    siblings,
    base.borrow?.acceptedCollateral ?? base.supply?.backedBy,
  )

  const merged = mergeDeep(base, opts.patch)

  // An adapter patch may REFINE a side the builder produced, or REMOVE one
  // (Compound V3 collateral rows set `borrow: undefined`) — but it must never
  // CREATE one. Whether a side exists at all is decided from the market data;
  // an adapter only describes terms. Without this guard a lender adapter that
  // patches `supply` on a borrow-only row leaves a half-built side with no
  // `info`, which crashes the headline pass — and it did, on pair legs.
  for (const side of ['supply', 'borrow'] as const) {
    if (merged[side] && !base[side]) merged[side] = undefined
  }

  const withInfo = finalizeInfo(merged)
  withInfo.coverage = buildCoverage(withInfo)
  return withInfo
}
